import Chip from '@material-ui/core/Chip';
import Box from '@mui/material/Box';
import clsx from 'clsx';
import CrossSellSKUCard from 'components-v2/mocules/CrossSellSKUCard';
import QuantityControlLite from 'components-v2/mocules/QuantityControlLite';
import QuantityControlViewed from 'components-v2/mocules/QuantityControlViewed';
import { VARIANT_LIST } from 'components-v2/ui/ConsultSpecialProductBtn';
import { QUICKORDER_CROSS_SELL_ICON, QUICKORDER_REPLACE_ICON, QUICKORDER_SUGGEST_ICON } from 'constants/Images';
import { getPathProductBySlug } from 'constants/Paths';
import { SVGPrice } from 'constants/SVG';
import useSubstituteCardTracking from 'hooks/useSubstituteCardTracking';
import Image from 'next/image';
import Link from 'next/link';
import { useRef } from 'react';
import { CrossSellItem, EnumHighProfitLayer, ProductReplace } from 'types/product';
import { ImageFallbackProductImage } from 'utils/ImageFallback';
import styles from './styles.module.css';

type QuantityVariant = 'lite' | 'quickOrder';

interface MainSKU {
  mainSKUSellerId?: number;
  mainSKUProductId?: string;
  mainSKU?: string;
  mainType?: string;
}

interface Props extends MainSKU {
  /** SPTT (DTO lite skuReplace) — hiện hết, xếp trước */
  substitutes?: ProductReplace[];
  /** SP hay mua kèm — BFF đã lọc + sort */
  crossSell?: CrossSellItem[];
  /** Cùng nghĩa với SubstituteSKUCard: 'quickOrder' = CartContext (PDP, ĐHN), 'lite' = store useCart (giỏ) */
  quantityVariant?: QuantityVariant;
  className?: string;
  /** id cho tour SPTT ở PDP (product__replace) */
  id?: string;
  /** PDP: bỏ đệm ngang để thẳng mép với tên sản phẩm; giỏ/ĐHN giữ đệm để tách khỏi card chính */
  flush?: boolean;
}

// Block "Gợi ý cho bạn" gộp SPTT + SP hay mua kèm thành 1 dãy tile dọc cuộn ngang.
// Chỉ dùng khi có SP mua kèm; không có thì host vẫn render block SPTT cũ.
const SuggestProductBlock = ({ substitutes, crossSell, quantityVariant = 'lite', className, id, flush = false, ...mainSKU }: Props) => {
  const listRef = useRef<HTMLDivElement>(null);
  // skuReplace ở giỏ có thể là null (không chỉ undefined) nên không dựa vào default param
  const subs = substitutes || [];
  const cross = crossSell || [];
  const total = subs.length + cross.length;
  if (total === 0) return null;
  // Chỉ 1 gợi ý (1 SP mua kèm, không SPTT) thì giữ card ngang như cũ; từ 2 trở lên mới ra dãy tile.
  // Trường hợp chỉ có SPTT thì host đã tự render block SPTT cũ.
  if (total === 1 && cross.length === 1) return <CrossSellSKUCard className={className} items={cross} quantityVariant={quantityVariant} />;

  const scrollBy = (dir: 1 | -1) => listRef.current?.scrollBy({ left: dir * listRef.current.clientWidth, behavior: 'smooth' });

  return (
    <div id={id} className={clsx(styles.block, { [styles.flush]: flush }, className)}>
      <Box className={styles.header}>
        <Image src={QUICKORDER_SUGGEST_ICON} width={24} height={24} loading="lazy" alt="suggest icon" />
        Gợi ý cho bạn
      </Box>
      <Box className={styles.listWrap}>
        <button type="button" aria-label="Cuộn trái" className={clsx(styles.arrow, styles.arrowLeft)} onClick={() => scrollBy(-1)}>
          ‹
        </button>
        <div ref={listRef} className={styles.list}>
          {subs.map((item) => (
            <SubstituteTile key={`sub-${item.skuCode}`} item={item} quantityVariant={quantityVariant} {...mainSKU} />
          ))}
          {cross.map((item) => (
            <Tile key={`cs-${item.skuCode}`} item={item} variant="crossSell" quantityVariant={quantityVariant} linkHref={getPathProductBySlug(item.slug)} />
          ))}
        </div>
        <button type="button" aria-label="Cuộn phải" className={clsx(styles.arrow, styles.arrowRight)} onClick={() => scrollBy(1)}>
          ›
        </button>
      </Box>
    </div>
  );
};

interface SubstituteTileProps extends MainSKU {
  item: ProductReplace;
  quantityVariant: QuantityVariant;
}

// Tile SPTT: giữ nguyên tracking + style lợi nhuận cao của SubstituteSKUCard
const SubstituteTile = ({ item, quantityVariant, mainSKUSellerId, mainSKUProductId, mainSKU, mainType }: SubstituteTileProps) => {
  const { impressionRef, handleDetailTracking, handleAddToCartTracking } = useSubstituteCardTracking({
    skuReplace: item,
    mainSKUSellerId,
    mainSKUProductId,
    mainSKU,
    mainType,
  });
  const linkHref =
    getPathProductBySlug(item.slug) +
    `?isSKUReplace=true&sellerIdMainSKU=${mainSKUSellerId}&productIdMainSKU=${mainSKUProductId}&mainSKU=${mainSKU}&mainType=${mainType || 'NORMAL'}`;
  return (
    <Tile
      item={item}
      variant="substitute"
      quantityVariant={quantityVariant}
      linkHref={linkHref}
      impressionRef={impressionRef}
      onDetailClick={handleDetailTracking}
      onAddToCart={handleAddToCartTracking}
    />
  );
};

interface TileProps {
  item: ProductReplace | CrossSellItem;
  variant: 'substitute' | 'crossSell';
  quantityVariant: QuantityVariant;
  linkHref: string;
  impressionRef?: React.RefObject<HTMLDivElement>;
  onDetailClick?: () => void;
  onAddToCart?: () => void;
}

const Tile = ({ item, variant, quantityVariant, linkHref, impressionRef, onDetailClick, onAddToCart }: TileProps) => {
  const { productId, productName, imageUrl, volume, price, originPrice = 0, discountPercent = 0, limitMessage = '', isHighProfit, styleDisplay, sellerName, sellerUrl } = item;
  // QuantityControlViewed / useCartQuickOrder đọc id / name / currentPrice / isDeal — map như SubstituteSKUCard
  const quickOrderProduct = { ...item, id: productId, name: productName, currentPrice: price, isDeal: item.type === 'DEAL' };
  const [headColor, middleColor, endColor] = styleDisplay?.web?.background?.colors || [];
  const [headPercentColor, middlePercentColor, endPercentColor] = styleDisplay?.web?.background?.percentsColor || [0, 0.5, 1];
  const angle = styleDisplay?.web?.background?.angle || 180;

  return (
    <div
      ref={impressionRef}
      className={clsx(
        styles.tile,
        { ['highProfitDoubleLayer']: isHighProfit && styleDisplay?.web?.background?.colors?.length === EnumHighProfitLayer.DOUBLE_LAYER },
        { ['highProfitTribleLayer']: isHighProfit && styleDisplay?.web?.background?.colors?.length === EnumHighProfitLayer.TRIBLE_LAYER },
      )}
      style={
        {
          '--high-profit-head-color': headColor ?? undefined,
          '--high-profit-middle-color': middleColor ?? undefined,
          '--high-profit-end-color': endColor ?? undefined,
          '--high-profit-angle': angle ? `${angle}deg` : undefined,
          '--high-profit-head-percent-color': headPercentColor !== undefined ? `${headPercentColor * 100}%` : undefined,
          '--high-profit-middle-percent-color': middlePercentColor !== undefined ? `${middlePercentColor * 100}%` : undefined,
          '--high-profit-end-percent-color': endPercentColor !== undefined ? `${endPercentColor * 100}%` : undefined,
        } as React.CSSProperties
      }
    >
      {variant === 'substitute' ? (
        <span className={clsx(styles.tag, styles.tagSubstitute)}>
          <Image src={QUICKORDER_REPLACE_ICON} width={14} height={14} loading="lazy" alt="replace icon" />
          Sản phẩm thay thế
        </span>
      ) : (
        <span className={clsx(styles.tag, styles.tagCrossSell)}>
          <Image src={QUICKORDER_CROSS_SELL_ICON} width={14} height={14} loading="lazy" alt="cross sell icon" />
          Sản phẩm mua kèm
        </span>
      )}
      <Box className={styles.image} onClick={onDetailClick}>
        <Link href={linkHref} prefetch={false}>
          <ImageFallbackProductImage
            src={imageUrl}
            alt={productName}
            width={96}
            height={96}
            quality={100}
            title={productName}
            loading="lazy"
            objectFit="contain"
            size="thumbnail"
          />
        </Link>
      </Box>
      <Box className={styles.priceRow}>
        <SVGPrice displayPriceFormated={price} size="md" />
        {discountPercent > 0 && originPrice > 0 && (
          <>
            <SVGPrice displayPriceFormated={originPrice} fillPath="#333" size="xs" line />
            <Chip className={styles.chip} label={`-${discountPercent}%`} size="small" />
          </>
        )}
      </Box>
      <Link href={linkHref} prefetch={false} passHref>
        <a className={styles.name} onClick={onDetailClick}>
          {productName}
        </a>
      </Link>
      <span className={styles.volume}>{volume}</span>
      {sellerName && (
        <a className={styles.seller} href={sellerUrl} target="_blank" rel="noreferrer">
          {sellerName}
        </a>
      )}
      <Box className={styles.action}>
        {!!limitMessage && <span className={styles.limit}>{limitMessage}</span>}
        {quantityVariant === 'quickOrder' ? (
          <QuantityControlViewed className={styles.quantityControlQuickOrder} product={quickOrderProduct} newCard onAdd={onAddToCart} onChange={onAddToCart} />
        ) : (
          <QuantityControlLite
            className={styles.quantityControlCart}
            product={item}
            newCard
            consultVariant={VARIANT_LIST.LIST_HORIZONTAL}
            onAdd={onAddToCart}
            onChange={onAddToCart}
          />
        )}
      </Box>
    </div>
  );
};

export default SuggestProductBlock;
